import { get, groupBy, rest, sum } from "lodash";
import { format, startOfToday, startOfMonth } from "date-fns";

export const mergeNodes = (nodes) => {
  const mergedMap = new Map();
  nodes.forEach((node) => {
    node.forEach(({ key, value }) => {
      if (mergedMap.has(key)) {
        mergedMap.set(key, mergedMap.get(key) + value);
      } else {
        mergedMap.set(key, value);
      }
    });
  });
  return Array.from(mergedMap, ([key, value]) => ({ key, value }));
};

export const getScenarioLeafs = (scenarios, filter = (_) => true) => {
  const leafs = [];
  for (const [key, value] of Object.entries(scenarios)) {
    if (key === "_variables") continue;
    if (value.path == null) {
      getScenarioLeafs(value, filter).forEach((leaf) => leafs.push(leaf));
    } else if (filter(value)) leafs.push(value);
  }
  return leafs;
};

export const getVariablesByName = (scenarios, indent = 0) => {
  console.log(`[${indent}]getVariablesByName`, { scenarios });
  if (Array.isArray(scenarios)) {
    console.log(`[${indent}]getVariablesByName is array`, { scenarios });
    return {};
  } else if (typeof scenarios === "object" && scenarios.path == null) {
    const { _variables, ...rest } = scenarios;
    const variables = Object.fromEntries(_variables?.map((v) => [v.id, v.default]) ?? []);
    const childVariables = Object.entries(rest)
      .map(([_, value]) => {
        return Object.entries(getVariablesByName(value, indent + 1));
      })
      .flat();
    const result = { ...variables, ...Object.fromEntries(childVariables) };

    if (_variables) console.log(`[${indent}]getVariablesByName is Folder`, { indent, variables, _variables, childVariables, result });
    else console.log(`[${indent}]getVariablesByName is Folder`, { indent, childVariables, result });

    return result;
  } else {
    console.log(`[${indent}]getVariablesByName is Other`, { scenarios });
    return {};
  }
};

export const mergeValues = (values) =>
  Object.entries(groupBy(values, (v) => v[0])).map(([key, group]) => [key, sum(group.map((x) => x[1]))]);

export const mergeTrees = (treeA, treeB, isLeaf, mergeNode, path = []) => {
  const merged = {};
  const keys = new Set([...Object.keys(treeA ?? {}), ...Object.keys(treeB ?? {})]);
  for (const key of keys) {
    const nodeA = treeA ? treeA[key] : undefined;
    const nodeB = treeB ? treeB[key] : undefined;
    if ((isLeaf(nodeA) || nodeA == null) && (isLeaf(nodeB) || nodeB == null)) {
      merged[key] = mergeNode(nodeA, nodeB);
    } else if (isLeaf(nodeA) || isLeaf(nodeB)) {
      throw new Error("Both nodes must be leaves or none: " + [...path, key].join("->"));
    } else {
      merged[key] = mergeTrees(nodeA, nodeB, isLeaf, mergeNode, [...path, key]);
    }
  }
  return merged;
};

export class Cashflow {
  values(columns) {
    return [];
  }
}

export class ValueCashflow extends Cashflow {
  constructor(value) {
    super();
    this.value = value;
  }

  values(columns) {
    return columns.filter((c) => !c.key.endsWith("_settled")).map((c) => [c.key, this.value]);
  }
}

export class MovementsCashflow extends Cashflow {
  constructor(movements, today) {
    super();
    this.movements = movements;
    this.today = today ?? startOfToday();
  }
  getColumn(date, status) {
    return startOfMonth(date).getTime() === startOfMonth(this.today).getTime()
      ? format(date, "yyyyMM") + "_" + (status === "settled" ? "settled" : "unsettled")
      : format(date, "yyyyMM");
  }

  values(columns) {
    const values = this.movements.map((m) => [this.getColumn(m.date, m.status), m.value]);
    return mergeValues(values).map(([key, value]) => [key, value]);
  }
}

export class RangesCashflow extends Cashflow {
  constructor(sign, ranges) {
    super();
    this.sign = sign;
    this.ranges = ranges;
  }

  values(columns) {
    return mergeValues(
      this.ranges.flatMap((v) => {
        if (v.month != null) {
          const key = columns.find((c) => c.key === v.month || c.key === v.month + "_unsettled");
          if (key) return [[key.key, this.sign * v.value]];
          else return [];
        } else {
          return columns.map((c) =>
            ((c.key.length === 6 && c.key) >= v.from || (c.key.length > 6 && c.key === v.from + "_unsettled") || v.from == null) &&
            (c.key <= v.to || c.key === v.to + "_unsettled" || v.to == null)
              ? [c.key, this.sign * v.value]
              : [c.key, 0]
          );
        }
      })
    );
  }
}
export class CompositeCashflow extends Cashflow {
  constructor(cashflows) {
    super();
    this.cashflows = cashflows;
  }

  values(columns) {
    const allValues = this.cashflows.filter((cf) => cf != null).flatMap((cf) => cf.values(columns));
    return mergeValues(allValues);
  }
}

export const buildCashflowsFromMilestones = (milestones, pathFunction, today) =>
  milestones.map((m) => ({ path: pathFunction(m), value: new MovementsCashflow([m], today) }));

export const buildCashflowsFromScenarios = (scenarios, variablesByName) =>
  scenarios.map((s) => ({
    path: s.path,
    value:
      typeof s.value === "number"
        ? new ValueCashflow((s.path[0] === "Outcomes" ? -1 : 1) * s.value)
        : typeof s.value === "function"
        ? new RangesCashflow(
            s.path[0] === "Outcomes" ? -1 : 1,
            s.value(
              s.options?.map((o) => o.value ?? o.default),
              variablesByName
            )
          )
        : new RangesCashflow(s.path[0] === "Outcomes" ? -1 : 1, s.value),
    people:
      s.people == null
        ? null
        : typeof s.people === "number"
        ? new ValueCashflow((s.path[0] === "Outcomes" ? 1 : -1) * s.people)
        : typeof s.people === "function"
        ? new RangesCashflow(
            s.path[0] === "Outcomes" ? 1 : -1,
            s.people(
              s.options?.map((o) => o.value ?? o.default),
              variablesByName
            )
          )
        : new RangesCashflow(s.path[0] === "Outcomes" ? 1 : -1, s.people),
  }));

export const buildCashflowsFromOutcomes = (outcomes) =>
  flattenTree(outcomes).map((s) => ({
    path: s.path,
    value:
      s.value == null
        ? null
        : typeof s.value === "number"
        ? new ValueCashflow((s.path[0] === "Outcomes" ? -1 : 1) * s.value)
        : new RangesCashflow(s.path[0] === "Outcomes" ? -1 : 1, s.value),
    people:
      s.people == null
        ? null
        : typeof s.people === "number"
        ? new ValueCashflow((s.path[0] === "Outcomes" ? 1 : -1) * s.people)
        : new RangesCashflow(s.path[0] === "Outcomes" ? 1 : -1, s.people),
  }));

export const flattenTree = (tree, path = []) => {
  if (typeof tree === "number" || Array.isArray(tree)) return [{ path: [...path], value: tree }];
  else if (tree.money != null || tree.people != null) {
    return [{ path: [...path], value: tree.money, people: tree.people }];
  } else {
    const entries = [];
    for (const [field, node] of Object.entries(tree)) entries.push(...flattenTree(node, [...path, field]));
    return entries;
  }
};

export const mergeCashflowsByPath = (cashflows) =>
  Object.entries(groupBy(cashflows, (x) => x.path)).map(([path, cashflows]) => ({
    path: cashflows[0].path,
    value: cashflows.length === 1 ? cashflows[0].value : new CompositeCashflow(cashflows.map((x) => x.value)),
    people: cashflows.length === 1 ? cashflows[0].people : new CompositeCashflow(cashflows.map((x) => x.people)),
  }));

export const buildTreeFromCashflows = (cashflows, columns) => {
  console.log({ cashflows, columns });
  const tree = {};
  cashflows.forEach((cf) => {
    let node = tree;
    cf.path.forEach((p, i) => {
      if (i === cf.path.length - 1) {
        node[p] = { money: cf.value?.values(columns) ?? null, people: cf.people?.values(columns) ?? null };
      } else {
        if (!node[p]) node[p] = {};
        node = node[p];
      }
    });
  });
  return tree;
};
